// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import Multiselect from '../../Multiselect';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { getErrorHelperText, isValueEmpty } from '../../../../utils/Utils';
import useAttributes from '../../../../hooks/Configuration/AttributeLibrary/useAttributes';
const handleMultiSelectOptionEqualToValue = (option, val) =>
  option?.value === val?.value;

const handleMultiselectGetOptionLabel = (option) => option?.label;

/**
 * MultiSelect field component for selecting multiple items from a list
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Array} props.value - Current field values
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @returns {React.ReactElement} The MultiSelect component
 */
const MultiSelectFieldComponent = ({
  label,
  titleLabel,
  value = EMPTY_ARRAY,
  onChange,
  attribute,
  entityDetailsFormikForm,
  overrideDropdownOptions = null,
  isLoadingConstraints = false,
  configColumn = false,
}) => {
  const { getSingleAttributeDetails } = useAttributes();
  const [fetchedOptions, setFetchedOptions] = useState(null);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);

  const multiSelectOptions = useMemo(() => {
    let optionsToUse = [];
    if (!isValueEmpty(overrideDropdownOptions)) {
      optionsToUse = overrideDropdownOptions;
    } else if (!isValueEmpty(attribute?.drop_down_options)) {
      optionsToUse = attribute?.drop_down_options;
    } else if (fetchedOptions !== null) {
      optionsToUse = fetchedOptions;
    }

    // Ensure optionsToUse is always an array before calling .map()
    optionsToUse = Array.isArray(optionsToUse) ? optionsToUse : [];

    const mapped =
      optionsToUse?.length > 0
        ? optionsToUse?.map((entity) => {
            // Handle both string values and option objects
            if (typeof entity === 'object') {
              return {
                label: entity?.label || entity?.value,
                value: entity?.value,
              };
            }
            return { label: entity, value: entity };
          })
        : [];

    // Deduplicate options by value to prevent filtering issues
    const seen = new Set();
    return mapped?.filter((opt) => {
      if (seen?.has(opt?.value)) {
        return false;
      }
      seen?.add(opt?.value);
      return true;
    });
  }, [fetchedOptions, overrideDropdownOptions, attribute?.drop_down_options]);

  const multiselectValue = useMemo(() => {
    return isEmpty(value) || !Array.isArray(value) ? [] : value;
  }, [value]);

  const handleRenderMultiSelectInput = useCallback(
    (params) => (
      <TextField
        {...params}
        name={attribute?.id}
        onBlur={entityDetailsFormikForm?.handleBlur}
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        label={label}
        title={titleLabel}
        placeholder="Select value"
        spellCheck={true}
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors[attribute?.id],
            entityDetailsFormikForm?.touched[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )}
      />
    ),
    [label, titleLabel, entityDetailsFormikForm, attribute?.id]
  );

  const handleChangeMultiSelect = useCallback(
    (newValue) => {
      if (isValueEmpty(newValue)) {
        onChange(null);
      } else {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const handleOpen = useCallback(async () => {
    // Only fetch if we have no options and haven't fetched yet
    if (
      multiSelectOptions.length === 0 &&
      attribute?.id &&
      fetchedOptions === null &&
      configColumn
    ) {
      setIsFetchingOptions(true);
      try {
        const res = await getSingleAttributeDetails(attribute.id);
        const enrichedOptions = res?.data?.drop_down_options || [];
        setFetchedOptions(enrichedOptions);
      } catch (_err) {
        setFetchedOptions([]);
      } finally {
        setIsFetchingOptions(false);
      }
    }
  }, [
    multiSelectOptions.length,
    attribute?.id,
    fetchedOptions,
    getSingleAttributeDetails,
    configColumn,
  ]);

  return (
    <Multiselect
      options={multiSelectOptions}
      value={multiselectValue}
      getOptionLabel={handleMultiselectGetOptionLabel}
      isOptionEqualToValue={handleMultiSelectOptionEqualToValue}
      renderInput={handleRenderMultiSelectInput}
      onChange={handleChangeMultiSelect}
      onOpen={handleOpen}
      disabled={isLoadingConstraints}
      loading={isLoadingConstraints || isFetchingOptions}
      loadingText="Loading options..."
    />
  );
};

MultiSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
  ),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    drop_down_options: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    handleBlur: PropTypes.func,
  }),
  overrideDropdownOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  isLoadingConstraints: PropTypes.bool,
  configColumn: PropTypes.bool,
};

export default MultiSelectFieldComponent;
